// external
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, FormControl, MenuItem, Select, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import dayjs, { Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';

// internal
import TrainingsService from '../../services/trainings.service';
import useService from '../../hooks/service.hook';
import useSnackbarHelper from '../../hooks/snackbar.hook';
import { getMeasurementUnit } from '../../helpers/general.helpers';
import { getDefaultWeek, getIsoWeekApiDateRange } from '../../helpers/datetime.helpers';
import COEDateRangeComponent from '../coe-date-range/coe-date-range.component';
import COETrainingCard from '../coe-training-card-2/coe-training-card-2.component';
import CoachAthleteTrainingModal from '../coach-athlete-training-modal';
import { AthleteScheduleItem } from '../athlete-member-bottom-sheet/athlete-member-bottom-sheet.component';
import { Group, Team } from '../../models/training.models';
import { palette } from '../../theme';
import '../athlete-member-bottom-sheet/athlete-member-bottom-sheet.component.css';
import './athlete-schedule-panel.component.css';

export interface AthleteSchedulePanelProps {
  team: Team;
  memberId: string;
  memberName?: string;
  memberPreview?: { name?: string; profileImage?: string | null };
}

interface WeekDay {
  key: string;
  dayName: string;
  dayNum: string;
  isToday: boolean;
  dateKey: string;
}

const formatDurationHoursMinutes = (seconds: number): string => {
  if (!seconds) return '0:00 hr';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}:${minutes.toString().padStart(2, '0')} hr`;
};

const AthleteSchedulePanel: FC<AthleteSchedulePanelProps> = (props) => {
  const trainingsService = useService(TrainingsService);
  const { showSnackbar } = useSnackbarHelper();
  const navigate = useNavigate();

  const teamId = String(props.team.id ?? props.team.teamId);
  const groups: Group[] = props.team.groups ?? [];

  const [week, setWeek] = useState<Dayjs>(getDefaultWeek);
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [scheduleData, setScheduleData] = useState<AthleteScheduleItem[]>([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [scheduleError, setScheduleError] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<AthleteScheduleItem | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const scheduleRange = useMemo(() => {
    const { start, end } = getIsoWeekApiDateRange(week);
    return { startTime: start, endTime: end };
  }, [week]);

  useEffect(() => {
    if (!props.memberId || !teamId) {
      setScheduleData([]);
      setScheduleError(false);
      return;
    }

    setScheduleLoading(true);
    setScheduleError(false);

    const payload: {
      startTime: string;
      endTime: string;
      team?: string;
      group?: string;
    } = {
      startTime: scheduleRange.startTime,
      endTime: scheduleRange.endTime,
    };

    if (teamFilter === 'this') {
      payload.team = teamId;
    }
    if (groupFilter !== 'all') {
      payload.group = groupFilter;
    }

    trainingsService
      .getAthleteSchedule(teamId, props.memberId, payload)
      .then((response: any) => {
        setScheduleData(response.data?.data ?? []);
        setScheduleLoading(false);
      })
      .catch((err) => {
        setScheduleLoading(false);
        setScheduleError(true);
        setScheduleData([]);
        const status = err?.response?.status;
        if (status === 403) {
          showSnackbar("You do not have permission to view this athlete's schedule.", 'error');
        } else if (status === 404) {
          showSnackbar('Athlete schedule not found.', 'error');
        } else {
          showSnackbar('There was an error loading the athlete schedule.', 'error');
        }
      });
  }, [props.memberId, teamId, scheduleRange.startTime, scheduleRange.endTime, teamFilter, groupFilter, refreshKey]);

  const weekDays = useMemo((): WeekDay[] => {
    const todayKey = dayjs().format('ddd MMM D');
    return Array.from({ length: 7 }).map((_, index) => {
      const date = week.startOf('week').add(index + 1, 'day');
      const dateKey = date.format('ddd MMM D');
      return {
        key: date.format('YYYY-MM-DD'),
        dayName: date.format('ddd').toUpperCase(),
        dayNum: date.format('D'),
        isToday: dateKey === todayKey,
        dateKey,
      };
    });
  }, [week]);

  const trainingsByDay = useMemo(() => {
    const grouped: Record<string, AthleteScheduleItem[]> = {};
    weekDays.forEach((day) => {
      grouped[day.dateKey] = scheduleData.filter((item) => dayjs(item.time).format('ddd MMM D') === day.dateKey);
    });
    return grouped;
  }, [scheduleData, weekDays]);

  const weeklyStats = useMemo(() => {
    const totalSessions = scheduleData.length;
    const totalCompletedSessions = scheduleData.filter((t) => t.complete).length;
    const totalPlannedDistance = scheduleData.reduce((sum, t) => sum + (t.totalPlannedDistance ?? 0), 0);
    const totalCompletedDistance = scheduleData.reduce((sum, t) => sum + (t.totalCompletedDistance ?? 0), 0);
    const totalPlannedDuration = scheduleData.reduce((sum, t) => sum + (t.totalPlannedDuration ?? 0), 0);
    const totalCompletedDuration = scheduleData.reduce((sum, t) => sum + (t.totalCompletedDuration ?? 0), 0);
    return {
      totalSessions,
      totalCompletedSessions,
      totalPlannedDistance,
      totalCompletedDistance,
      totalPlannedDuration,
      totalCompletedDuration,
    };
  }, [scheduleData]);

  const handleToday = () => {
    setWeek(getDefaultWeek());
  };

  const handleNewWorkout = () => {
    navigate(`/teams/${teamId}/team-trainings`);
  };

  return (
    <Box className="athlete-schedule-panel">
      <Box className="as-control-bar">
        <Box className="as-control-left">
          <COEDateRangeComponent value={week} setValue={(val: Dayjs) => setWeek(val)} />
          <FormControl size="small" className="as-filter-select">
            <Select
              value={teamFilter}
              onChange={(e) => setTeamFilter(e.target.value)}
              displayEmpty
              sx={{ fontSize: 13, borderRadius: '10px', minWidth: 120 }}
            >
              <MenuItem value="all">All Teams</MenuItem>
              <MenuItem value="this">{props.team.name || 'This Team'}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" className="as-filter-select">
            <Select
              value={groupFilter}
              onChange={(e) => setGroupFilter(e.target.value)}
              displayEmpty
              sx={{ fontSize: 13, borderRadius: '10px', minWidth: 120 }}
            >
              <MenuItem value="all">All Groups</MenuItem>
              {groups.map((group) => (
                <MenuItem key={String(group.id)} value={String(group.id)}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box className="as-control-right">
          <Button
            variant="outlined"
            size="small"
            onClick={handleToday}
            sx={{
              textTransform: 'none',
              borderColor: palette.divider,
              color: palette.text.primary,
              borderRadius: '10px',
              fontWeight: 600,
            }}
          >
            Today
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleNewWorkout}
            sx={{
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 600,
              bgcolor: palette.primary.main,
            }}
          >
            New Workout
          </Button>
        </Box>
      </Box>

      <Box className="weekly-summary-bar as-summary-bar">
        <Box className="ws-item">
          <div className="ws-label">Sessions</div>
          <div className="ws-value">
            <span style={{ color: palette.primary.main }}>{weeklyStats.totalCompletedSessions}</span>{' '}
            <span className="muted">/ {weeklyStats.totalSessions}</span>
          </div>
        </Box>
        <Box className="ws-item">
          <div className="ws-label">Distance</div>
          <div className="ws-value">
            <span style={{ color: palette.primary.main }}>
              {weeklyStats.totalCompletedDistance.toFixed(0)} {getMeasurementUnit()}
            </span>{' '}
            <span className="muted">
              / {weeklyStats.totalPlannedDistance.toFixed(0)} {getMeasurementUnit()}
            </span>
          </div>
        </Box>
        <Box className="ws-item">
          <div className="ws-label">Duration</div>
          <div className="ws-value">
            <span style={{ color: palette.primary.main }}>
              {formatDurationHoursMinutes(weeklyStats.totalCompletedDuration)}
            </span>{' '}
            <span className="muted">/ {formatDurationHoursMinutes(weeklyStats.totalPlannedDuration)}</span>
          </div>
        </Box>
      </Box>

      <Box className="athlete-member-bottom-sheet-schedule-section as-schedule-section">
        {scheduleLoading ? (
          <Box className="athlete-member-bottom-sheet-loading" sx={{ minHeight: 200 }}>
            <CircularProgress size={40} />
          </Box>
        ) : scheduleError ? (
          <Typography variant="body2" color="error" className="week-grid-empty">
            There was an error loading the schedule. Please try again.
          </Typography>
        ) : scheduleData.length === 0 ? (
          <Typography variant="body2" color="text.secondary" className="week-grid-empty">
            No trainings available for this week
          </Typography>
        ) : (
          <div className="week-grid as-week-grid">
            {weekDays.map((day) => (
              <div key={day.key} className={`day-col${day.isToday ? ' day-today' : ''}`}>
                <div className="day-hdr">
                  <div className={`day-hdr-name${day.isToday ? ' day-hdr-name--today' : ''}`}>{day.dayName}</div>
                  {day.isToday ? (
                    <div className="day-hdr-num day-hdr-num--today">{day.dayNum}</div>
                  ) : (
                    <div className="day-hdr-num">{day.dayNum}</div>
                  )}
                </div>
                <div className="day-col-cards">
                  {trainingsByDay[day.dateKey]?.map((training) => (
                    <COETrainingCard
                      key={training.id}
                      title={training.title}
                      time={training.time}
                      location={training.location}
                      type={training.type}
                      viewerType="athlete"
                      team={training.team}
                      group={training.group}
                      complete={training.complete}
                      completedDistance={training.totalCompletedDistance}
                      completedDuration={training.totalCompletedDuration}
                      plannedDistance={training.totalPlannedDistance}
                      plannedDuration={training.totalPlannedDuration}
                      onClick={() => setSelectedTraining(training)}
                      isWatchUpload={training.isWatchUpload}
                      rsvpStatus={training.rsvpStatus ?? undefined}
                      unreadCommentsCount={training.unreadComments}
                      athleteName={training.type === 'personal' ? props.memberName : undefined}
                      isSelected={selectedTraining?.id === training.id}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Box>

      <CoachAthleteTrainingModal
        open={selectedTraining !== null}
        onClose={() => setSelectedTraining(null)}
        teamId={teamId}
        memberId={props.memberId}
        trainingId={selectedTraining?.id ?? null}
        memberPreview={{
          name: props.memberPreview?.name ?? props.memberName ?? 'Athlete',
          profileImage: props.memberPreview?.profileImage,
        }}
        onTrainingUpdated={() => setRefreshKey((k) => k + 1)}
      />
    </Box>
  );
};

export default AthleteSchedulePanel;
